import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      company: null,
      selectedCompany: null, // For Super/Managing Admins to work within a company context
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      _hasHydrated: false, // Track if persist has rehydrated
      
      // Set loading state
      setLoading: (loading) => set({ isLoading: loading }),
      
      // Check if user is a super user (Super Admin or Managing Admin)
      isSuperUser: () => {
        const { user } = get();
        return user && ['SUPER_ADMIN', 'MANAGING_ADMIN'].includes(user.role);
      },
      
      // Get the current working company (selected or user's own)
      getCurrentCompany: () => {
        const { user, company, selectedCompany } = get();
        if (user && ['SUPER_ADMIN', 'MANAGING_ADMIN'].includes(user.role)) {
          return selectedCompany || company;
        }
        return company;
      },
      
      // Get the current company ID for API calls
      getCurrentCompanyId: () => {
        const company = get().getCurrentCompany();
        return company?._id || company;
      },
      
      // Select a company to work within (for Super/Managing Admins)
      selectCompany: (company) => {
        set({ selectedCompany: company });
      },
      
      // Clear selected company
      clearSelectedCompany: () => {
        set({ selectedCompany: null });
      },
      
      // Login
      login: async (email, password) => {
        try {
          const response = await api.post('/auth/login', { email, password });
          const { user, company, accessToken, refreshToken } = response.data;
          
          set({
            user,
            company,
            accessToken,
            refreshToken,
            isAuthenticated: true,
            isLoading: false
          });
          
          return { success: true };
        } catch (error) {
          return { 
            success: false, 
            error: error.response?.data?.error || 'Login failed' 
          };
        }
      },
      
      // Logout
      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          set({
            user: null,
            company: null,
            selectedCompany: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false
          });
        }
      },
      
      // Refresh token
      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          set({ isAuthenticated: false, isLoading: false });
          return false;
        }
        
        try {
          const response = await api.post('/auth/refresh-token', { refreshToken });
          const { accessToken, refreshToken: newRefreshToken } = response.data;
          
          set({
            accessToken,
            refreshToken: newRefreshToken,
            isAuthenticated: true,
            isLoading: false
          });
          
          return true;
        } catch (error) {
          set({
            user: null,
            company: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false
          });
          return false;
        }
      },
      
      // Fetch current user
      fetchUser: async () => {
        try {
          const response = await api.get('/auth/me');
          const { user, company } = response.data;
          
          set({ user, company, isLoading: false });
          return { success: true };
        } catch (error) {
          if (error.response?.status === 401) {
            // Try to refresh token
            const refreshed = await get().refreshAccessToken();
            if (refreshed) {
              return get().fetchUser();
            }
          }
          set({ isLoading: false });
          return { success: false };
        }
      },
      
      // Update user preferences
      updatePreferences: (preferences) => {
        const { user } = get();
        if (user) {
          set({
            user: {
              ...user,
              preferences: { ...user.preferences, ...preferences }
            }
          });
        }
      },
      
      // Check if user has permission
      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;
        
        // Super admins have all permissions
        if (user.role === 'SUPER_ADMIN') return true;
        if (user.role === 'MANAGING_ADMIN' && permission !== 'delete_super_admin') return true;
        
        // Check custom permissions
        if (user.customPermissions?.granted?.includes(permission)) return true;
        if (user.customPermissions?.revoked?.includes(permission)) return false;
        
        // Role-based permissions would be checked here
        // For simplicity, we'll do basic checks
        const rolePermissions = {
          ADMIN: ['manage_users', 'manage_data_sources', 'create_dashboards', 'edit_dashboards', 'view_dashboards', 'export', 'use_ai'],
          DATA_ANALYST: ['create_dashboards', 'edit_dashboards', 'share_dashboards', 'view_dashboards', 'export', 'use_ai'],
          VIEWER: ['view_dashboards', 'filter_data']
        };
        
        return rolePermissions[user.role]?.includes(permission) || false;
      },
      
      // Initialize auth state (called after rehydration)
      initialize: async () => {
        const { accessToken, _hasHydrated } = get();
        
        // Don't initialize until hydration is complete
        if (!_hasHydrated) {
          return;
        }
        
        if (!accessToken) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }
        
        // We have a token, so assume authenticated until proven otherwise
        set({ isAuthenticated: true });
        
        // Fetch user to validate the token and get user data
        const result = await get().fetchUser();
        if (!result.success) {
          // Token was invalid, clear auth state
          set({ isAuthenticated: false, accessToken: null, refreshToken: null });
        }
      },
      
      // Mark hydration complete
      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      }
    }),
    {
      name: 'neoshift-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        selectedCompany: state.selectedCompany
      }),
      onRehydrateStorage: () => (state) => {
        // Called when the storage has been rehydrated
        if (state) {
          state.setHasHydrated(true);
          state.initialize();
        }
      }
    }
  )
);

